/**
 * Matching and ranking shared by every global-search source.
 *
 * Substring, not fuzzy. Fuzzy matching earns its keep over large corpora of
 * similar names; here the corpus is a few hundred company names and a fixed
 * list of page labels, where it mostly produces confident nonsense. A typo
 * returning nothing is easier to recover from than a typo returning the wrong
 * company.
 */

export const SCORE_EXACT = 1000;
export const SCORE_PREFIX = 500;
export const SCORE_WORD_START = 250;
export const SCORE_SUBSTRING = 100;
/** Matched only via a synonym, never shown to the user, so it ranks under any label hit. */
export const SCORE_SYNONYM = 40;

export function normalize(value: string): string {
  return value.toLowerCase().normalize("NFKD").replace(/[\u0300-\u036f]/g, "");
}

/**
 * Where `query` hits `text`, and how good the hit is.
 * Returns null when there is no match at all.
 */
export function matchText(
  text: string,
  query: string,
): { score: number; range: [number, number] } | null {
  if (!query) return null;
  const haystack = normalize(text);
  const needle = normalize(query);
  const at = haystack.indexOf(needle);
  if (at === -1) return null;

  const range: [number, number] = [at, at + needle.length];
  if (haystack === needle) return { score: SCORE_EXACT, range };
  if (at === 0) return { score: SCORE_PREFIX, range };
  // A hit right after a space or separator reads as "found the word", which is
  // much stronger than landing mid-word ("ads" inside "leads").
  const isWordStart = /[\s\-_/&.,(]/.test(haystack[at - 1] ?? "");
  return { score: isWordStart ? SCORE_WORD_START : SCORE_SUBSTRING, range };
}

/**
 * Best hit across a label and its hidden synonyms. A synonym hit reports no
 * range: the matched text is not on screen, so there is nothing to bold, and
 * inventing a range would highlight unrelated characters in the label.
 */
export function matchLabel(
  label: string,
  query: string,
  synonyms: readonly string[] = [],
): { score: number; matches: [number, number][] } | null {
  const direct = matchText(label, query);
  if (direct) return { score: direct.score, matches: [direct.range] };

  for (const synonym of synonyms) {
    if (matchText(synonym, query)) return { score: SCORE_SYNONYM, matches: [] };
  }
  return null;
}

/** Stable sort: score first, then shorter titles, then alphabetical. */
export function compareResults(
  a: { score: number; title: string },
  b: { score: number; title: string },
): number {
  if (a.score !== b.score) return b.score - a.score;
  if (a.title.length !== b.title.length) return a.title.length - b.title.length;
  return a.title.localeCompare(b.title);
}

/** Split a title into bold/plain runs for rendering. */
export function highlight(
  title: string,
  matches: [number, number][],
): { text: string; bold: boolean }[] {
  if (matches.length === 0) return [{ text: title, bold: false }];
  const [start, end] = matches[0];
  const parts: { text: string; bold: boolean }[] = [];
  if (start > 0) parts.push({ text: title.slice(0, start), bold: false });
  parts.push({ text: title.slice(start, end), bold: true });
  if (end < title.length) parts.push({ text: title.slice(end), bold: false });
  return parts;
}
